import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { openOnRowClick } from '../utils/rowClick';
import {
    Users,
    UserPlus,
    Search,
    Mail,
    Phone,
    MapPin,
    Eye,
    Edit2,
    TrendingUp,
    UserCheck,
    Building2,
    Calendar,
    BookOpen,
    Star,
    Plus,
    Upload,
    Download,
    FileSpreadsheet,
    Globe,
    CheckCircle,
    AlertTriangle,
    Trash2,
    MoreHorizontal,
    X,
} from 'lucide-react';
import { studentsApi, campusApi, classesApi } from '../services/api';
import {
    ActionMenu,
    Button,
    DataToolbar,
    DetailFooter,
    DetailHeader,
    DetailItem,
    DetailList,
    DetailSection,
    DetailStats,
    Input,
    LocalPagination,
    Modal,
    ResponsiveDataView,
    Select,
    StatusBadge,
    Textarea,
    useLocalPagination,
    useToast,
    DateInput,
    todayDateKey,
    SearchSelect,
} from '../components/ui';
import { classPickerOptions, scheduleSummary } from '../utils/pickerOptions';
import { PageHeader } from '../components/PageHeader';
import { StudentGradebook } from '../components/students/StudentGradebook';
import { matchesQuery } from '../utils/search';

type DeleteDialogState =
    | null
    | { mode: 'single'; student: any }
    | { mode: 'selected' }
    | { mode: 'all' };

const STUDENTS_PAGE_SIZE = 10;

const Students = () => {
    const { hasPermission } = usePermissions();
    const { user } = useAuth();
    const { showToast: showUiToast } = useToast();
    const canDeleteStudents = user?.role === 'ADMIN';
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCampus, setSelectedCampus] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedClass, setSelectedClass] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    // Wide screens show a student's details beside the list instead of in a modal.
    const showDetailPanel = useMediaQuery('(min-width: 1280px)');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddClassModal, setShowAddClassModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<any>(null);
    const [students, setStudents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [campusOptions, setCampusOptions] = useState<any[]>([]);
    const [availableClasses, setAvailableClasses] = useState<any[]>([]);
    const [selectedClassToAdd, setSelectedClassToAdd] = useState('');
    const [importData, setImportData] = useState<any[]>([]);
    const [importResults, setImportResults] = useState<any>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importMethod, setImportMethod] = useState<'file' | 'googlesheets'>('file');
    const [file, setFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
    const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);

    // Function to load students data
    const loadStudents = async () => {
        try {
            const res = await studentsApi.getAll();
            setStudents(res.data.data || []);
        } catch (err) {
            console.error('Failed to load students:', err);
            notify('❌ Không thể tải danh sách học sinh!', 'error');
        }
    };

    // Function to load available classes for a student
    const loadAvailableClasses = async (studentId: string) => {
        try {
            const res = await classesApi.getAll();
            const allClasses = res.data.data || [];

            // Find student's current enrollments
            const student = students.find(s => s.id === studentId);
            // By id: two classes can share a name (e.g. one L10T1 per campus).
            const enrollments: any[] = student?.enrollments || [];
            const enrolledClassIds = new Set(enrollments.map(e => e.classId).filter(Boolean));
            const enrolledNames = new Set(enrollments.filter(e => !e.classId).map(e => e.className));

            // Filter out classes the student is already enrolled in
            const available = allClasses.filter(c => !enrolledClassIds.has(c.id) && !enrolledNames.has(c.name));

            setAvailableClasses(available);
        } catch (err) {
            console.error('Failed to load classes:', err);
            notify('❌ Không thể tải danh sách lớp học!', 'error');
        }
    };

    // Function to add student to a class
    const handleAddStudentToClass = async () => {
        if (!selectedClassToAdd || !selectedStudent) {
            notify('❌ Vui lòng chọn lớp học!', 'error');
            return;
        }

        try {
            await classesApi.enrollStudent(selectedClassToAdd, selectedStudent.id);
            notify('✅ Đã thêm học sinh vào lớp học!', 'success');

            // Refresh students data
            await loadStudents();

            // Trigger refresh for Classes page if it's open
            localStorage.setItem('enrollment_updated', Date.now().toString());

            // Close modal and reset
            setShowAddClassModal(false);
            setSelectedClassToAdd('');
        } catch (err: any) {
            notify(err.message || '❌ Không thể thêm học sinh vào lớp!', 'error');
        }
    };

    // Function to handle file change
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseFile(selectedFile);
        }
    };

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const droppedFile = files[0];

            // Check file type
            const allowedTypes = ['.csv', '.json', '.xlsx', '.xls'];
            const fileExtension = '.' + droppedFile.name.split('.').pop()?.toLowerCase();

            if (!allowedTypes.includes(fileExtension)) {
                notify('❌ Định dạng file không được hỗ trợ! Chỉ chấp nhận: CSV, JSON, XLSX, XLS', 'error');
                return;
            }

            // Check file size (10MB max)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (droppedFile.size > maxSize) {
                notify('❌ File quá lớn! Tối đa 10MB', 'error');
                return;
            }

            setFile(droppedFile);
            parseFile(droppedFile);
        }
    };

    // Function to split full name into first and last name
    const splitFullName = (fullName: string) => {
        if (!fullName) return { firstName: '', lastName: '' };

        const nameParts = fullName.trim().split(' ');
        if (nameParts.length === 1) {
            return { firstName: nameParts[0], lastName: '' };
        }

        // Vietnamese naming convention: [Họ] [Tên đệm] [Tên]
        // Last name is typically the last word, first name is the first word
        const lastName = nameParts[nameParts.length - 1];
        const firstName = nameParts.slice(0, -1).join(' ');

        return { firstName, lastName };
    };

    // Function to parse file
    const parseFile = async (file: File) => {
        try {
            let data: any[] = [];

            if (file.name.endsWith('.csv')) {
                const text = await file.text();
                data = parseCSV(text);
            } else if (file.name.endsWith('.json')) {
                const text = await file.text();
                const jsonData = JSON.parse(text);
                data = Array.isArray(jsonData) ? jsonData : [jsonData];
            } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                // Parse Excel files using SheetJS
                const arrayBuffer = await file.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                data = XLSX.utils.sheet_to_json(worksheet);
            }

            // Transform data to match our schema
            const transformedData = data.map((row) => {
                // Handle full name (new format) vs separate first/last name (old format)
                let firstName = '';
                let lastName = '';

                if (row['Họ và tên'] || row['Họ và Tên'] || row['Full Name']) {
                    const fullName = row['Họ và tên'] || row['Họ và Tên'] || row['Full Name'] || '';
                    const splitName = splitFullName(fullName);
                    firstName = splitName.firstName;
                    lastName = splitName.lastName;
                } else {
                    // Fallback to separate columns (backward compatibility)
                    firstName = row['Tên'] || row['firstName'] || row['First Name'] || '';
                    lastName = row['Họ'] || row['lastName'] || row['Last Name'] || '';
                }

                return {
                    firstName,
                    lastName,
                    gender: row['Giới tính'] || row['gender'] || row['Gender'] || '', // Optional now
                    dateOfBirth: row['Ngày sinh'] || row['dateOfBirth'] || row['Date of Birth'] || '', // Optional now
                    phone: row['Số điện thoại'] || row['phone'] || row['Phone'] || '',
                    email: row['Email'] || row['email'] || '',
                    address: row['Địa chỉ'] || row['address'] || row['Address'] || '',
                    parentName: row['Tên phụ huynh'] || row['parentName'] || row['Parent Name'] || '',
                    parentPhone: row['SĐT phụ huynh'] || row['parentPhone'] || row['Parent Phone'] || ''
                };
            });

            // console.log('Parsed data sample:', transformedData.slice(0, 2)); // Debug log
            setImportData(transformedData);
            setShowPreview(true);
            notify(`✅ Đã đọc ${transformedData.length} học sinh từ file!`, 'success');
        } catch (error) {
            console.error('Parse file error:', error);
            notify('❌ Lỗi khi đọc file!', 'error');
        }
    };

    // Function to parse CSV
    const parseCSV = (text: string): any[] => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) return [];

        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const data = [];

        for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
            const item: any = {};

            headers.forEach((header, index) => {
                item[header] = values[index] || '';
            });

            data.push(item);
        }

        return data;
    };

    // Function to handle Google Sheets URL
    const handleGoogleSheetsUrl = async () => {
        if (!googleSheetsUrl.trim()) {
            notify('❌ Vui lòng nhập link Google Sheets!', 'error');
            return;
        }

        try {
            let csvUrl = googleSheetsUrl.trim();

            // Convert Google Sheets URL to CSV export URL
            if (googleSheetsUrl.includes('docs.google.com/spreadsheets')) {
                const match = googleSheetsUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
                if (!match) {
                    notify('❌ URL Google Sheets không hợp lệ!', 'error');
                    return;
                }
                csvUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv`;
            }

            notify('🔄 Đang tải dữ liệu từ Google Sheets...', 'info');

            const response = await fetch(csvUrl);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const text = await response.text();
            const data = parseCSV(text);

            if (data.length === 0) {
                notify('❌ Không tìm thấy dữ liệu trong Google Sheets!', 'error');
                return;
            }

            // console.log('Google Sheets raw data:', data.slice(0, 2)); // Debug log

            // Transform data to match our schema (same logic as parseFile)
            const transformedData = data.map((row) => {
                // Handle full name (new format) vs separate first/last name (old format)
                let firstName = '';
                let lastName = '';

                if (row['Họ và tên'] || row['Họ và Tên'] || row['Full Name']) {
                    const fullName = row['Họ và tên'] || row['Họ và Tên'] || row['Full Name'] || '';
                    const splitName = splitFullName(fullName);
                    firstName = splitName.firstName;
                    lastName = splitName.lastName;
                } else {
                    // Fallback to separate columns (backward compatibility)
                    firstName = row['Tên'] || row['firstName'] || row['First Name'] || '';
                    lastName = row['Họ'] || row['lastName'] || row['Last Name'] || '';
                }

                return {
                    firstName,
                    lastName,
                    gender: row['Giới tính'] || row['gender'] || row['Gender'] || '', // Optional now
                    dateOfBirth: row['Ngày sinh'] || row['dateOfBirth'] || row['Date of Birth'] || '', // Optional now
                    phone: row['Số điện thoại'] || row['phone'] || row['Phone'] || '',
                    email: row['Email'] || row['email'] || '',
                    address: row['Địa chỉ'] || row['address'] || row['Address'] || '',
                    parentName: row['Tên phụ huynh'] || row['parentName'] || row['Parent Name'] || '',
                    parentPhone: row['SĐT phụ huynh'] || row['parentPhone'] || row['Parent Phone'] || ''
                };
            });

            // console.log('Google Sheets transformed data:', transformedData.slice(0, 2)); // Debug log

            setImportData(transformedData);
            setShowPreview(true);
            notify(`✅ Đã tải ${transformedData.length} học sinh từ Google Sheets!`, 'success');
        } catch (error: any) {
            console.error('Google Sheets error:', error);

            if (error.message.includes('CORS') || error.message.includes('blocked')) {
                notify('❌ Không thể truy cập Google Sheets. Vui lòng kiểm tra quyền chia sẻ!', 'error');
            } else if (error.message.includes('404')) {
                notify('❌ Không tìm thấy Google Sheets. Kiểm tra link có đúng không!', 'error');
            } else {
                notify(`❌ Lỗi khi tải từ Google Sheets: ${error.message}`, 'error');
            }
        }
    };

    // Function to handle import
    const handleImport = async () => {
        if (importData.length === 0) {
            notify('❌ Không có dữ liệu để nhập!', 'error');
            return;
        }

        // Validate and clean data before sending
        const validData = importData.filter((item, index) => {
            if (!item.firstName || !item.firstName.trim() || !item.lastName || !item.lastName.trim()) {
                console.warn(`Row ${index + 1}: Missing firstName or lastName`, item);
                return false;
            }
            return true;
        }).map(item => ({
            ...item,
            firstName: item.firstName.trim(),
            lastName: item.lastName.trim(),
            gender: item.gender || '',
            dateOfBirth: item.dateOfBirth || '',
            phone: item.phone || '',
            email: item.email || '',
            address: item.address || '',
            parentName: item.parentName || '',
            parentPhone: item.parentPhone || ''
        }));

        if (validData.length === 0) {
            notify('❌ Không có dữ liệu hợp lệ để nhập! Vui lòng kiểm tra lại họ tên.', 'error');
            return;
        }

        // console.log('Import data being sent:', validData.slice(0, 2)); // Debug log
        // console.log(`Filtered ${importData.length - validData.length} invalid rows`);

        setIsImporting(true);
        try {
            const response = await studentsApi.bulkImport(validData);
            // console.log('Import response:', response.data); // Debug log
            setImportResults(response.data.data);
            notify(response.data.message, 'success');

            // Refresh students list
            await loadStudents();
        } catch (err: any) {
            console.error('Import error:', err); // Debug log
            notify(err.message || '❌ Nhập dữ liệu thất bại!', 'error');
        } finally {
            setIsImporting(false);
        }
    };

    // Function to reset import state
    const resetImport = () => {
        setFile(null);
        setGoogleSheetsUrl('');
        setImportData([]);
        setShowPreview(false);
        setImportResults(null);
        setIsImporting(false);
        setImportMethod('file');
        setIsDragOver(false);
    };

    // Function to download template
    const downloadTemplate = () => {
        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();

        // Template data with merged full name and optional gender/birthDate
        const templateData = [
            {
                'Họ và tên': 'Nguyễn Văn A',
                'Giới tính': 'Nam',
                'Ngày sinh': '2005-01-15',
                'Số điện thoại': '0123456789',
                'Email': 'nguyenvana@email.com',
                'Địa chỉ': '123 Đường ABC',
                'Tên phụ huynh': 'Nguyễn Văn B',
                'SĐT phụ huynh': '0987654321'
            },
            {
                'Họ và tên': 'Trần Thị C',
                'Giới tính': 'Nữ',
                'Ngày sinh': '2005-03-20',
                'Số điện thoại': '0123456790',
                'Email': 'tranthic@email.com',
                'Địa chỉ': '456 Đường XYZ',
                'Tên phụ huynh': 'Trần Văn D',
                'SĐT phụ huynh': '0987654322'
            },
            {
                'Họ và tên': 'Lê Minh E',
                'Giới tính': '',
                'Ngày sinh': '',
                'Số điện thoại': '',
                'Email': '',
                'Địa chỉ': '789 Đường GHI',
                'Tên phụ huynh': 'Lê Văn F',
                'SĐT phụ huynh': '0987654323'
            }
        ];

        // Create worksheet from data
        const ws = XLSX.utils.json_to_sheet(templateData);

        // Auto-fit column widths
        const colWidths = [
            { wch: 20 }, // Họ và tên
            { wch: 10 }, // Giới tính
            { wch: 12 }, // Ngày sinh
            { wch: 15 }, // Số điện thoại
            { wch: 25 }, // Email
            { wch: 30 }, // Địa chỉ
            { wch: 20 }, // Tên phụ huynh
            { wch: 15 }  // SĐT phụ huynh
        ];
        ws['!cols'] = colWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Template Học sinh');

        // Save file
        XLSX.writeFile(wb, 'template_danh_sach_hoc_sinh.xlsx');
    };



    // Initial load
    useEffect(() => {
        loadStudents().finally(() => setLoading(false));
    }, []);

    // Auto-refresh when window gets focus or becomes visible
    useEffect(() => {
        const handleFocus = () => {
            // Refresh students data when window gets focus
            loadStudents();
        };

        const handleVisibilityChange = () => {
            // Refresh when tab becomes visible
            if (!document.hidden) {
                loadStudents();
            }
        };

        const handleStorageChange = (e: StorageEvent) => {
            // Refresh when enrollment data changes in other tabs/pages
            if (e.key === 'enrollment_updated' || e.key === 'students_updated') {
                loadStudents();
                // Clear the flag after refreshing
                localStorage.removeItem(e.key || '');
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    useEffect(() => {
        campusApi.getAll()
            .then(res => {
                const active = (res.data.data || []).filter((c: any) => c.isActive);
                setCampusOptions(active);
            });
    }, []);

    // Form state
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        phone: '',
        email: '',
        address: '',
        parentName: '',
        parentPhone: ''
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // useEffect to set form data when edit modal opens
    useEffect(() => {
        if (showEditModal && selectedStudent) {
            const nameParts = (selectedStudent.name || '').split(' ');
            let dateOfBirth = '';

            if (selectedStudent.birthday) {
                try {
                    const date = new Date(selectedStudent.birthday);
                    const year = date.getUTCFullYear();
                    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(date.getUTCDate()).padStart(2, '0');
                    dateOfBirth = `${year}-${month}-${day}`;
                } catch (e) {
                    console.error('Invalid date format:', selectedStudent.birthday);
                }
            }

            const newFormData = {
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                gender: selectedStudent.gender || '',
                dateOfBirth: dateOfBirth,
                phone: selectedStudent.phone || '',
                email: selectedStudent.email || '',
                address: selectedStudent.address || '',
                parentName: selectedStudent.parentName || '',
                parentPhone: selectedStudent.parentPhone || ''
            };

            setFormData(newFormData);
            setFormErrors({}); // Clear any previous errors
        }
    }, [showEditModal, selectedStudent]);

    // Get unique values for filters
    const campuses = campusOptions.map(c => c.name);
    const allClasses = students.flatMap(s => (s.enrollments || []).map((e: any) => e.className)).filter((v, i, a) => a.indexOf(v) === i)
        .sort((a, b) => a.localeCompare(b, 'vi', { numeric: true }));

    const getStudentStatusText = (status: string) => status === 'active' ? 'Đang học' : 'Nghỉ học';
    const getStudentStatusTone = (status: string) => status === 'active' ? 'success' : 'neutral';

    const handleDropdownAction = (action: string, student: any) => {
        // Find the latest data from students list to ensure we have complete data
        const latestStudent = students.find(s => s.id === student.id) || student;

        // Defensive check to ensure we have valid student data
        if (!latestStudent || !latestStudent.id) {
            notify('❌ Không thể tải thông tin học sinh!', 'error');
            return;
        }

        setSelectedStudent(latestStudent);

        switch (action) {
            case 'view':
                setShowViewModal(true);
                break;
            case 'edit':
                setShowEditModal(true);
                setShowForm(true);
                break;
            case 'add-class':
                loadAvailableClasses(latestStudent.id);
                setShowAddClassModal(true);
                break;
        }
    };

    // Individual handler functions for table view
    const handleViewStudent = (student: any) => {
        handleDropdownAction('view', student);
    };

    const handleEditStudent = (student: any) => {
        handleDropdownAction('edit', student);
    };

    const handleManageClasses = (student: any) => {
        handleDropdownAction('add-class', student);
    };

    const actionMenuId = (surface: 'desktop' | 'mobile', studentId: string) => `${surface}-${studentId}`;

    const closeActionMenu = () => {
        setOpenActionMenu(null);
    };

    const renderStudentActionMenu = (student: any, surface: 'desktop' | 'mobile') => {
        const studentName = student.name || 'chưa có tên';
        const menuId = actionMenuId(surface, student.id);
        const isOpen = openActionMenu === menuId;

        return (
            <ActionMenu
                open={isOpen}
                onOpenChange={(open) => setOpenActionMenu(open ? menuId : null)}
                triggerLabel={`Tuỳ chỉnh học sinh ${studentName}`}
                triggerTitle="Tuỳ chỉnh"
                triggerIcon={<MoreHorizontal className="h-4 w-4" />}
                buttonVariant={surface === 'desktop' ? 'ghost' : 'secondary'}
                buttonSize="icon"
                width={208}
            >
                        <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-ink-100"
                            aria-label={`Chỉnh sửa học sinh ${studentName}`}
                            onClick={() => {
                                closeActionMenu();
                                handleEditStudent(student);
                            }}
                        >
                            <Edit2 className="h-4 w-4" />
                            Chỉnh sửa
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-ink-100"
                            aria-label={`Quản lý lớp học cho ${studentName}`}
                            onClick={() => {
                                closeActionMenu();
                                handleManageClasses(student);
                            }}
                        >
                            <BookOpen className="h-4 w-4" />
                            Lớp học
                        </button>
                        {canDeleteStudents ? (
                            <button
                                type="button"
                                role="menuitem"
                                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-danger-700 hover:bg-danger-50"
                                aria-label={`Xóa học sinh ${studentName}`}
                                onClick={() => {
                                    closeActionMenu();
                                    setDeleteDialog({ mode: 'single', student });
                                }}
                            >
                                <Trash2 className="h-4 w-4" />
                                Xóa
                            </button>
                        ) : null}
            </ActionMenu>
        );
    };

    const notify = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        showUiToast(message, type);
    };

    // Form validation
    const validateForm = (data: typeof formData): Record<string, string> => {
        const errors: Record<string, string> = {};

        if (!data.firstName.trim()) errors.firstName = 'Tên là bắt buộc';
        if (!data.lastName.trim()) errors.lastName = 'Họ là bắt buộc';

        // Gender and dateOfBirth are now optional - no validation needed

        // Phone is optional, only validate format if provided
        if (data.phone && data.phone.trim() && !/^[0-9]{10,11}$/.test(data.phone.trim())) {
            errors.phone = 'Số điện thoại không hợp lệ (10-11 chữ số)';
        }

        // Email is optional, only validate format if provided
        if (data.email && data.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
            errors.email = 'Email không hợp lệ';
        }

        return errors;
    };

    // Form submission
    const handleSubmit = async (isEdit: boolean = false) => {
        const errors = validateForm(formData);
        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            notify('❌ Vui lòng kiểm tra lại thông tin!', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            let result;
            if (isEdit) {
                result = await studentsApi.update(selectedStudent.id, formData);
                const updatedStudent = result.data.data;
                // Merge with existing data to preserve enrollments if they're not returned
                const mergedStudent = {
                    ...selectedStudent,
                    ...updatedStudent,
                    enrollments: updatedStudent.enrollments || selectedStudent.enrollments || []
                };
                // Update students list
                setStudents(prev => prev.map(s => s.id === selectedStudent.id ? mergedStudent : s));
                // Update selectedStudent to reflect the new data
                setSelectedStudent(mergedStudent);
                notify('✅ Cập nhật học sinh thành công!', 'success');
                setShowEditModal(false);
                setShowForm(false);
            } else {
                result = await studentsApi.create(formData);
                // Add new student to list
                setStudents(prev => [...prev, result.data.data]);
                notify('✅ Thêm học sinh thành công!', 'success');
                setShowForm(false);
            }

            // Reset form
            setFormData({
                firstName: '', lastName: '', gender: '', dateOfBirth: '', phone: '', email: '',
                address: '', parentName: '', parentPhone: ''
            });
            setFormErrors({});
        } catch (err) {
            console.error('Failed to save student:', err);
            notify('❌ Không thể lưu thông tin học sinh!', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeImportModal = () => {
        setShowImportModal(false);
        resetImport();
    };

    const closeStudentFormModal = () => {
        setShowForm(false);
        setShowEditModal(false);
        setFormData({
            firstName: '',
            lastName: '',
            gender: '',
            dateOfBirth: '',
            phone: '',
            email: '',
            address: '',
            parentName: '',
            parentPhone: ''
        });
        setFormErrors({});
    };

    const closeAddClassModal = () => {
        setShowAddClassModal(false);
        setSelectedClassToAdd('');
    };

    // Filtering
    const filteredStudents = students.filter(student => {
        const matchesSearch = matchesQuery([student.name, student.studentId, student.email, student.phone, student.parentName, student.parentPhone].filter(Boolean).join(' '), searchTerm);

        const matchesCampus = selectedCampus === 'all' ||
            (student.enrollments || []).some((e: any) => e.campusName === selectedCampus);

        const matchesClass = selectedClass === 'all' ||
            (student.enrollments || []).some((e: any) => e.className === selectedClass);

        const matchesStatus = selectedStatus === 'all' || student.status === selectedStatus;

        return matchesSearch && matchesCampus && matchesClass && matchesStatus;
    });
    const {
        page: studentsPage,
        pageSize: studentsPageSize,
        pagedItems: pagedStudents,
        setPage: setStudentsPage,
        setPageSize: setStudentsPageSize,
    } = useLocalPagination(
        filteredStudents,
        STUDENTS_PAGE_SIZE,
        `${searchTerm}|${selectedCampus}|${selectedClass}|${selectedStatus}`,
    );

    const selectedFilteredStudentIds = filteredStudents
        .map(student => student.id)
        .filter((id): id is string => Boolean(id));
    const allFilteredStudentsSelected = selectedFilteredStudentIds.length > 0
        && selectedFilteredStudentIds.every(id => selectedStudentIds.includes(id));

    const toggleStudentSelection = (studentId: string) => {
        setSelectedStudentIds(prev =>
            prev.includes(studentId)
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        );
    };

    const toggleAllFilteredStudents = () => {
        setSelectedStudentIds(prev => {
            if (allFilteredStudentsSelected) {
                return prev.filter(id => !selectedFilteredStudentIds.includes(id));
            }

            return [...new Set([...prev, ...selectedFilteredStudentIds])];
        });
    };

    const clearDeleteDialog = () => {
        if (isDeleting) return;
        setDeleteDialog(null);
    };

    const getDeleteDialogCopy = () => {
        if (!deleteDialog) {
            return {
                title: '',
                description: '',
                confirmLabel: '',
            };
        }

        if (deleteDialog.mode === 'single') {
            const name = deleteDialog.student?.name || 'học sinh này';
            return {
                title: 'Xóa học sinh',
                description: `Bạn sắp xóa ${name}. Hành động này không thể hoàn tác.`,
                confirmLabel: 'Xóa học sinh',
            };
        }

        if (deleteDialog.mode === 'selected') {
            return {
                title: 'Xóa học sinh đã chọn',
                description: `Bạn sắp xóa ${selectedStudentIds.length} học sinh đã chọn. Hành động này không thể hoàn tác.`,
                confirmLabel: 'Xóa học sinh đã chọn',
            };
        }

        return {
            title: 'Xóa tất cả học sinh',
            description: `Bạn sắp xóa toàn bộ ${students.length} học sinh trong hệ thống. Hành động này không thể hoàn tác.`,
            confirmLabel: 'Xóa tất cả học sinh',
        };
    };

    const handleConfirmDelete = async () => {
        if (!deleteDialog) return;

        setIsDeleting(true);
        try {
            if (deleteDialog.mode === 'single') {
                await studentsApi.delete(deleteDialog.student.id);
                notify('✅ Đã xóa học sinh!', 'success');
            } else if (deleteDialog.mode === 'selected') {
                await studentsApi.bulkDelete(selectedStudentIds);
                notify('✅ Đã xóa học sinh đã chọn!', 'success');
            } else {
                await studentsApi.deleteAll();
                notify('✅ Đã xóa tất cả học sinh!', 'success');
            }

            setSelectedStudentIds([]);
            setDeleteDialog(null);
            localStorage.setItem('students_updated', Date.now().toString());
            await loadStudents();
        } catch (err: any) {
            notify(err?.message || '❌ Không thể xóa học sinh!', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const deleteDialogCopy = getDeleteDialogCopy();

    // Stats
    const renderStudentDetail = (compact: boolean) => {
        if (!selectedStudent) return null;
        return (
            <div className="space-y-4">
                {compact ? (
                <StatusBadge tone={getStudentStatusTone(selectedStudent.status)}>
                    {getStudentStatusText(selectedStudent.status)}
                </StatusBadge>
            ) : (
            <DetailHeader
                    icon={(selectedStudent.name || '').split(' ').map((n: string) => n[0]).join('').slice(0, 2) || '??'}
                    title={selectedStudent.name}
                    subtitle={selectedStudent.studentId ? `Mã học sinh: ${selectedStudent.studentId}` : undefined}
                    badge={(
                        <StatusBadge tone={getStudentStatusTone(selectedStudent.status)}>
                            {getStudentStatusText(selectedStudent.status)}
                        </StatusBadge>
                    )}
                />
            )}
                <DetailStats items={[
                    { label: 'Lớp học', value: (selectedStudent.enrollments || []).length },
                    { label: 'Điểm danh', value: `${selectedStudent.overallAttendance || 0}%` },
                    { label: 'Điểm trung bình', value: selectedStudent.averageGrade || 0 },
                ]} />
                <div className={compact ? 'space-y-4' : 'grid gap-4 md:grid-cols-2'}>
                    <DetailSection title="Thông tin cá nhân">
                        <DetailList>
                            <DetailItem icon={<Users />} label="Giới tính" value={selectedStudent.gender} />
                            <DetailItem icon={<Calendar />} label="Ngày sinh" value={selectedStudent.birthday ? new Date(selectedStudent.birthday).toLocaleDateString('vi-VN') : ''} />
                            <DetailItem icon={<Calendar />} label="Ngày nhập học" value={selectedStudent.enrollmentDate ? new Date(selectedStudent.enrollmentDate).toLocaleDateString('vi-VN') : ''} />
                        </DetailList>
                    </DetailSection>
                    <DetailSection title="Liên hệ">
                        <DetailList>
                            <DetailItem icon={<Phone />} label="Điện thoại" value={selectedStudent.phone} />
                            <DetailItem icon={<Mail />} label="Email" value={selectedStudent.email} />
                            <DetailItem icon={<MapPin />} label="Địa chỉ" value={selectedStudent.address} />
                        </DetailList>
                    </DetailSection>
                    <DetailSection title="Phụ huynh" className={compact ? undefined : 'md:col-span-2'}>
                        <div className={compact ? 'grid gap-3' : 'grid gap-3 sm:grid-cols-2'}>
                            <DetailItem icon={<UserCheck />} label="Họ tên" value={selectedStudent.parentName} />
                            <DetailItem icon={<Phone />} label="Điện thoại" value={selectedStudent.parentPhone} />
                        </div>
                    </DetailSection>
                    <DetailSection title={`Lớp đang học (${(selectedStudent.enrollments || []).length})`} className={compact ? undefined : 'md:col-span-2'}>
                        {(selectedStudent.enrollments || []).length === 0 ? (
                            <p className="text-sm text-ink-400">Chưa đăng ký lớp nào</p>
                        ) : (
                            <div className="space-y-4">
                                {Object.entries(
                                    (selectedStudent.enrollments || []).reduce((acc: Record<string, any[]>, enrollment: any) => {
                                        const key = enrollment.campusName || 'Chưa có cơ sở';
                                        (acc[key] ||= []).push(enrollment);
                                        return acc;
                                    }, {})
                                ).map(([campusName, campusEnrollments]) => (
                                    <div key={campusName} className="space-y-2">
                                        <p className="flex items-center gap-2 text-sm font-medium text-ink-700">
                                            <Building2 className="h-4 w-4 text-ink-400" />
                                            {campusName}
                                            <span className="font-normal text-ink-500">({(campusEnrollments as any[]).length} lớp)</span>
                                        </p>
                                        {(campusEnrollments as any[]).map((enrollment: any, index: number) => (
                                            <div key={index} className="rounded-lg border border-ink-200 bg-ink-50 p-3">
                                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                                    <p className="font-semibold text-ink-900">{enrollment.className || 'Chưa có tên lớp'}</p>
                                                    <p className="text-xs text-ink-500">{enrollment.subject || 'Chưa có môn học'}</p>
                                                </div>
                                                <div className={compact ? 'mt-2 grid grid-cols-2 gap-3' : 'mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4'}>
                                                    <DetailItem label="Giáo viên" value={enrollment.teacher} />
                                                    <DetailItem label="Phòng học" value={enrollment.room} />
                                                    <DetailItem label="Lịch học" value={scheduleSummary(enrollment.scheduleEntries)} />
                                                    <DetailItem label="Điểm danh" value={`${enrollment.attendanceRate || 0}%`} />
                                                    <DetailItem label="Điểm số" value={enrollment.grade || 0} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </DetailSection>
                    <DetailSection title="Sổ điểm" className={compact ? undefined : 'md:col-span-2'}>
                        <StudentGradebook studentId={selectedStudent.id} />
                    </DetailSection>
                </div>
            </div>
        );
    };

    const activeStudentCount = students.filter(s => s.status === 'active').length;

    if (loading) {
        return (
            <div className="space-y-6">
                <PageHeader title="Học sinh" />
                <div className="flex items-center justify-center py-12">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-200 border-t-academy-600"></div>
                    <span className="ml-3 text-sm text-ink-500">Đang tải danh sách học sinh...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <PageHeader
                title="Học sinh"
                subtitle={`${students.length} học sinh · ${activeStudentCount} đang học`}
                actions={hasPermission('student', 'create') ? (
                    <>
                            <Button
                                variant="secondary"
                                icon={<Upload className="h-4 w-4" />}
                                onClick={() => {
                                    resetImport();
                                    setShowImportModal(true);
                                }}
                            >
                                Nhập danh sách học sinh
                            </Button>
                            <Button
                                icon={<UserPlus className="h-4 w-4" />}
                                onClick={() => setShowForm(true)}
                            >
                                Thêm học sinh
                            </Button>
                    </>
                ) : null}
            />

            <DataToolbar
                className="sm:items-center"
                controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center"
                actions={(
                    <>
                        {canDeleteStudents && selectedStudentIds.length > 0 ? (
                            <Button
                                variant="danger"
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => setDeleteDialog({ mode: 'selected' })}
                            >
                                Xóa đã chọn
                            </Button>
                        ) : null}
                        {canDeleteStudents && students.length > 0 ? (
                            <Button
                                variant="secondary"
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => setDeleteDialog({ mode: 'all' })}
                            >
                                Xóa tất cả
                            </Button>
                        ) : null}
                    </>
                )}
            >
                <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tìm tên, mã học sinh…"
                    leftIcon={<Search className="h-4 w-4" />}
                    aria-label="Tìm kiếm học sinh"
                    className="min-h-9 lg:w-72"
                />
                <Select
                    className="min-h-9 lg:w-44"
                    value={selectedCampus}
                    onChange={(e) => setSelectedCampus(e.target.value)}
                    aria-label="Lọc theo cơ sở"
                >
                    <option value="all">Tất cả cơ sở</option>
                    {campuses.map(campus => (
                        <option key={campus} value={campus}>{campus}</option>
                    ))}
                </Select>
                <SearchSelect
                    className="min-h-9 lg:w-44"
                    value={selectedClass === 'all' ? '' : selectedClass}
                    onChange={value => setSelectedClass(value || 'all')}
                    aria-label="Lọc theo lớp học"
                    emptyOption="Tất cả lớp"
                    options={allClasses.map(className => ({ value: className, label: className }))}
                />
                <Select
                    className="min-h-9 lg:w-44"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    aria-label="Lọc theo trạng thái"
                >
                    <option value="all">Mọi trạng thái</option>
                    <option value="active">Đang học</option>
                    <option value="inactive">Nghỉ học</option>
                </Select>
            </DataToolbar>

            {/* Students list, with the selected student's details beside it on wide screens */}
            <div className="flex items-start gap-5">
            <div className="min-w-0 flex-1 lg:overflow-hidden lg:rounded-lg lg:border lg:border-ink-200 lg:bg-white">
                {canDeleteStudents && selectedStudentIds.length > 0 ? (
                    <div className="border-b border-ink-200 bg-ink-50 px-4 py-2 text-sm font-medium text-danger-700">
                        Đã chọn {selectedStudentIds.length}
                    </div>
                ) : null}

                {filteredStudents.length === 0 ? (
                    <div className="p-8 text-center">
                        <h3 className="text-sm font-medium text-ink-900">Không có dữ liệu</h3>
                        <p className="mt-1 text-sm text-ink-500">
                            {searchTerm || selectedCampus !== 'all' || selectedClass !== 'all' || selectedStatus !== 'all'
                                ? 'Không tìm thấy học sinh phù hợp với bộ lọc.'
                                : 'Chưa có học sinh nào.'
                            }
                        </p>
                    </div>
                ) : (
                    <>
                    <ResponsiveDataView
                        desktopLabel="Bảng danh sách học sinh"
                        mobileLabel="Thẻ danh sách học sinh"
                        desktop={
                            <table className="min-w-full divide-y divide-ink-200 text-sm">
                                <thead>
                                    <tr>
                                        {canDeleteStudents ? (
                                            <th className="w-10 py-2.5 pl-4 pr-1 text-left">
                                                <input
                                                    type="checkbox"
                                                    checked={allFilteredStudentsSelected}
                                                    onChange={toggleAllFilteredStudents}
                                                    aria-label="Chọn tất cả học sinh"
                                                    className="h-4 w-4 cursor-pointer rounded border-ink-300 text-academy-600 focus:ring-academy-200"
                                                />
                                            </th>
                                        ) : null}
                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-ink-500 pl-4">Mã</th>
                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-ink-500">Học sinh</th>
                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-ink-500">Lớp học</th>
                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-ink-500">Liên hệ</th>
                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-ink-500">Trạng thái</th>
                                        <th className="w-12 px-3 py-2.5"><span className="sr-only">Tuỳ chỉnh</span></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-ink-100">
                                    {pagedStudents.map((student) => {
                                        const isOpen = showViewModal && selectedStudent?.id === student.id;
                                        const enrollments = student.enrollments || [];
                                        return (
                                            <tr
                                                key={student.id}
                                                onClick={openOnRowClick(() => handleViewStudent(student))}
                                                className={`cursor-pointer transition-colors ${isOpen ? 'bg-academy-50' : 'hover:bg-ink-50'}`}
                                            >
                                                {canDeleteStudents ? (
                                                    <td className="py-2.5 pl-4 pr-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedStudentIds.includes(student.id)}
                                                            onChange={() => toggleStudentSelection(student.id)}
                                                            aria-label={`Chọn học sinh ${student.name || 'chưa có tên'}`}
                                                            className="h-4 w-4 cursor-pointer rounded border-ink-300 text-academy-600 focus:ring-academy-200"
                                                        />
                                                    </td>
                                                ) : null}
                                                <td className="whitespace-nowrap px-3 py-2.5 pl-4 text-xs tabular-nums text-ink-500">
                                                    {student.studentId || '—'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewStudent(student)}
                                                        aria-label={`Xem chi tiết học sinh ${student.name || 'chưa có tên'}`}
                                                        className="cursor-pointer text-left font-medium text-ink-900 hover:text-academy-600"
                                                    >
                                                        {student.name || 'Chưa có tên'}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2.5 text-ink-700">
                                                    {enrollments.length > 0 ? (
                                                        <span className="whitespace-nowrap">
                                                            {enrollments.slice(0, 2).map((enrollment: any) => enrollment.className).join(', ')}
                                                            {enrollments.length > 2 ? <span className="text-ink-500"> +{enrollments.length - 2}</span> : null}
                                                        </span>
                                                    ) : (
                                                        <span className="text-ink-400">Chưa có lớp</span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-ink-700">
                                                    {student.phone || student.parentPhone || student.email || <span className="text-ink-400">—</span>}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2.5">
                                                    <StatusBadge tone={getStudentStatusTone(student.status)}>
                                                        {getStudentStatusText(student.status)}
                                                    </StatusBadge>
                                                </td>
                                                <td className="px-3 py-1.5 text-right">
                                                    {renderStudentActionMenu(student, 'desktop')}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        }
                        mobile={
                            <div className="grid gap-3">
                                {pagedStudents.map((student) => (
                                    <article
                                        key={student.id}
                                        onClick={openOnRowClick(() => handleViewStudent(student))}
                                        className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4 transition-colors hover:bg-ink-50"
                                    >
                                        {/* Header */}
                                        <div className="mb-4 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                                            <div className="flex min-w-0 items-start gap-3">
                                                <div className="flex min-w-0 items-center gap-3">
                                                    {canDeleteStudents ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedStudentIds.includes(student.id)}
                                                            onChange={() => toggleStudentSelection(student.id)}
                                                            aria-label={`Chọn học sinh ${student.name || 'chưa có tên'}`}
                                                            className="h-4 w-4 shrink-0 cursor-pointer rounded border-academy-200 text-academy-600 focus:ring-academy-200"
                                                        />
                                                    ) : null}
                                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink-100">
                                                        <span className="text-sm font-semibold text-ink-700">
                                                            {(student.name || '').split(' ').map(n => n[0]).join('').slice(0, 2) || '??'}
                                                        </span>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h3 className="truncate text-base font-semibold text-ink-900">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleViewStudent(student)}
                                                                aria-label={`Xem chi tiết học sinh ${student.name || 'chưa có tên'}`}
                                                                className="max-w-full cursor-pointer truncate text-left"
                                                            >
                                                                {student.name || 'Chưa có tên'}
                                                            </button>
                                                        </h3>
                                                        <p className="truncate text-sm text-ink-500">{student.studentId || 'Chưa có mã'}</p>
                                                        <div className="mt-2">
                                                            <StatusBadge tone={getStudentStatusTone(student.status)}>
                                                                {getStudentStatusText(student.status)}
                                                            </StatusBadge>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                {renderStudentActionMenu(student, 'mobile')}
                                            </div>
                                        </div>

                                        {/* Contact Info */}
                                        {(student.email || student.phone || student.parentPhone) && (
                                            <div className="mb-4 space-y-2">
                                                {student.email && (
                                                    <div className="flex items-center text-sm text-ink-600">
                                                        <Mail className="w-4 h-4 mr-2" />
                                                        <span className="truncate">{student.email}</span>
                                                    </div>
                                                )}
                                                {(student.phone || student.parentPhone) && (
                                                    <div className="flex items-center text-sm text-ink-600">
                                                        <Phone className="w-4 h-4 mr-2" />
                                                        <span>{student.phone || student.parentPhone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Classes */}
                                        <div className="space-y-2">
                                            <div className="text-sm font-medium text-ink-700">Lớp học:</div>
                                            {(student.enrollments || []).length > 0 ? (
                                                <div className="space-y-1">
                                                    {(student.enrollments || []).slice(0, 3).map((enrollment: any, idx: number) => (
                                                        <div key={idx} className="flex items-center text-sm text-ink-600">
                                                            <BookOpen className="w-3 h-3 mr-2 text-academy-500" />
                                                            <span>{enrollment.className}</span>
                                                            {enrollment.campusName && (
                                                                <span className="ml-2 text-xs text-ink-400">({enrollment.campusName})</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {(student.enrollments || []).length > 3 && (
                                                        <div className="text-xs text-ink-500 ml-5">
                                                            +{(student.enrollments || []).length - 3} lớp khác
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-sm text-ink-400">Chưa có lớp học</div>
                                            )}
                                        </div>

                                    </article>
                                ))}
                            </div>
                        }
                    />
                    <LocalPagination
                        label="danh sách học sinh"
                        page={studentsPage}
                        pageSize={studentsPageSize}
                        totalItems={filteredStudents.length}
                        pageSizeOptions={[10, 20, 50]}
                        onPageChange={setStudentsPage}
                        onPageSizeChange={setStudentsPageSize}
                    />
                    </>
                )}
            </div>

            {showDetailPanel && showViewModal && selectedStudent ? (
                <aside
                    className="sticky top-20 w-[400px] shrink-0 overflow-hidden rounded-lg border border-ink-200 bg-white"
                    aria-label="Chi tiết học sinh"
                >
                    <div className="flex items-start justify-between gap-3 border-b border-ink-200 px-4 py-3">
                        <div className="min-w-0">
                            <h2 className="truncate text-base font-semibold text-ink-900">{selectedStudent.name || 'Chưa có tên'}</h2>
                            <p className="text-xs tabular-nums text-ink-500">{selectedStudent.studentId || 'Chưa có mã'}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                            {hasPermission('student', 'update') ? (
                                <Button size="sm" variant="secondary" className="min-h-8" onClick={() => handleDropdownAction('edit', selectedStudent)}>
                                    Sửa
                                </Button>
                            ) : null}
                            <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                icon={<X className="h-4 w-4" />}
                                aria-label="Đóng chi tiết học sinh"
                                onClick={() => setShowViewModal(false)}
                            />
                        </div>
                    </div>
                    <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-4">
                        {renderStudentDetail(true)}
                    </div>
                </aside>
            ) : null}
            </div>

            <Modal
                open={Boolean(deleteDialog)}
                title={deleteDialogCopy.title}
                description={deleteDialogCopy.description}
                onClose={clearDeleteDialog}
                footer={
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={clearDeleteDialog} disabled={isDeleting}>
                            Hủy
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmDelete}
                            loading={isDeleting}
                            icon={<Trash2 className="h-4 w-4" />}
                        >
                            {deleteDialogCopy.confirmLabel}
                        </Button>
                    </div>
                }
            >
                <div className="rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-800">
                    Hệ thống sẽ từ chối xóa nếu học sinh vẫn đang tham gia lớp học đang hoạt động.
                </div>
            </Modal>

            {/* View Details Modal */}
            {!showDetailPanel && showViewModal && selectedStudent && selectedStudent.name && (
                <Modal
                    open={showViewModal}
                    title="Chi tiết học sinh"
                    onClose={() => setShowViewModal(false)}
                    className="lg:max-w-3xl"
                    footer={(
                        <DetailFooter
                            onClose={() => setShowViewModal(false)}
                            onEdit={hasPermission('student', 'update') ? () => {
                                setShowViewModal(false);
                                handleDropdownAction('edit', selectedStudent);
                            } : undefined}
                        />
                    )}
                >
                    {renderStudentDetail(false)}
                </Modal>
            )}

            {/* Import Modal */}
            {showImportModal && (
                <Modal
                    open={showImportModal}
                    title="Nhập danh sách học sinh"
                    onClose={closeImportModal}
                    className="lg:max-w-4xl"
                    footer={
                        !showPreview && !importResults ? (
                            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <Button variant="secondary" onClick={closeImportModal}>
                                    Hủy
                                </Button>
                                {importMethod === 'file' && file && (
                                    <Button
                                        onClick={() => parseFile(file)}
                                        icon={<FileSpreadsheet className="h-4 w-4" />}
                                    >
                                        Phân tích file
                                    </Button>
                                )}
                                {importMethod === 'googlesheets' && googleSheetsUrl && (
                                    <Button
                                        onClick={handleGoogleSheetsUrl}
                                        icon={<Globe className="h-4 w-4" />}
                                    >
                                        Nhập dữ liệu
                                    </Button>
                                )}
                            </div>
                        ) : showPreview && !importResults ? (
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowPreview(false);
                                        setImportData([]);
                                    }}
                                >
                                    ← Quay lại
                                </Button>
                                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                    <Button variant="secondary" onClick={closeImportModal}>
                                        Hủy
                                    </Button>
                                    <Button
                                        onClick={handleImport}
                                        loading={isImporting}
                                        disabled={isImporting}
                                        icon={<Upload className="h-4 w-4" />}
                                    >
                                        {`Nhập ${importData.length} học sinh`}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-end">
                                <Button
                                    onClick={() => {
                                        closeImportModal();
                                        loadStudents();
                                    }}
                                >
                                    Hoàn thành
                                </Button>
                            </div>
                        )
                    }
                >
                            {!showPreview && !importResults ? (
                                <div className="space-y-6">
                                    {/* Method Selection */}
                                    <div>
                                        <h4 className="text-md font-semibold text-ink-900 mb-4">Chọn phương thức nhập</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <button
                                                onClick={() => setImportMethod('file')}
                                                className={`p-4 rounded-lg border-2 transition-all duration-200 ${importMethod === 'file'
                                                    ? 'border-academy-500 bg-academy-50'
                                                    : 'border-ink-200 hover:border-ink-300'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <FileSpreadsheet className="w-8 h-8 text-academy-600" />
                                                    <div className="text-left">
                                                        <h5 className="font-medium text-ink-900">Tải file</h5>
                                                        <p className="text-sm text-ink-600">CSV, Excel, JSON</p>
                                                    </div>
                                                </div>
                                            </button>

                                            <button
                                                onClick={() => setImportMethod('googlesheets')}
                                                className={`p-4 rounded-lg border-2 transition-all duration-200 ${importMethod === 'googlesheets'
                                                    ? 'border-academy-500 bg-academy-50'
                                                    : 'border-ink-200 hover:border-ink-300'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <Globe className="w-8 h-8 text-ink-500" />
                                                    <div className="text-left">
                                                        <h5 className="font-medium text-ink-900">Google Sheets</h5>
                                                        <p className="text-sm text-ink-600">Nhập từ link</p>
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Template Download */}
                                    <div className="bg-ink-50 border border-ink-200 rounded-lg p-4">
                                        <div className="flex items-start space-x-3">
                                            <Download className="w-5 h-5 text-ink-500 mt-0.5" />
                                            <div className="flex-1">
                                                <h5 className="font-medium text-ink-900">Tải file mẫu</h5>
                                                <p className="text-sm text-ink-600 mb-3">
                                                    Tải file mẫu để tham khảo định dạng và cách điền thông tin học sinh
                                                </p>
                                                <div className="text-sm text-ink-700 space-y-1 mb-3">
                                                    <p>• <strong>Bắt buộc:</strong> Họ và tên</p>
                                                    <p>• <strong>Không bắt buộc:</strong> Giới tính, Ngày sinh, Số điện thoại, Email, Địa chỉ, Tên phụ huynh, SĐT phụ huynh</p>
                                                    <p>• Định dạng ngày sinh: YYYY-MM-DD (ví dụ: 2005-01-15) - có thể để trống</p>
                                                    <p>• Giới tính: Nam hoặc Nữ - có thể để trống</p>
                                                    <p>• Họ và tên sẽ được tách tự động (ví dụ: "Nguyễn Văn A" → Họ: "Nguyễn Văn", Tên: "A")</p>
                                                </div>
                                                <button
                                                    onClick={downloadTemplate}
                                                    className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200"
                                                >
                                                    Tải file mẫu
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* File Upload */}
                                    {importMethod === 'file' && (
                                        <div>
                                            <label className="block text-sm font-medium text-ink-700 mb-2">
                                                Chọn file để nhập
                                            </label>
                                            <div
                                                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-all duration-200 ${isDragOver
                                                    ? 'border-academy-500 bg-academy-50'
                                                    : 'border-ink-300 hover:border-ink-400'
                                                    }`}
                                                onDrop={handleDrop}
                                                onDragOver={handleDragOver}
                                                onDragEnter={handleDragEnter}
                                                onDragLeave={handleDragLeave}
                                            >
                                                <div className="space-y-1 text-center">
                                                    <Upload className={`mx-auto h-12 w-12 transition-colors duration-200 ${isDragOver ? 'text-academy-500' : 'text-ink-400'
                                                        }`} />
                                                    <div className="flex text-sm text-ink-600">
                                                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-academy-600 hover:text-academy-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-academy-200">
                                                            <span>Tải file lên</span>
                                                            <input
                                                                type="file"
                                                                className="sr-only"
                                                                accept=".csv,.json,.xlsx,.xls"
                                                                onChange={handleFileChange}
                                                            />
                                                        </label>
                                                        <p className="pl-1">hoặc kéo thả</p>
                                                    </div>
                                                    <p className="text-xs text-ink-500">
                                                        CSV, JSON, XLSX, XLS tối đa 10MB
                                                    </p>
                                                    {isDragOver && (
                                                        <p className="text-sm text-academy-600 font-medium">
                                                            Thả file vào đây để tải lên
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {file && (
                                                <div className="mt-2 text-sm text-ink-600">
                                                    File đã chọn: {file.name}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Google Sheets URL */}
                                    {importMethod === 'googlesheets' && (
                                        <div>
                                            <label className="block text-sm font-medium text-ink-700 mb-2">
                                                Link Google Sheets
                                            </label>
                                            <div className="space-y-3">
                                                <input
                                                    type="url"
                                                    value={googleSheetsUrl}
                                                    onChange={(e) => setGoogleSheetsUrl(e.target.value)}
                                                    placeholder="https://docs.google.com/spreadsheets/d/..."
                                                    className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-academy-500"
                                                />
                                                <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                                                    <div className="flex items-start space-x-2">
                                                        <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5" />
                                                        <div className="text-sm text-warning-800">
                                                            <p className="font-medium">Lưu ý quan trọng:</p>
                                                            <p>Google Sheets phải được công khai hoặc chia sẻ với chế độ "Anyone with the link can view"</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            ) : showPreview && !importResults ? (
                                <div className="space-y-6">
                                    <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                                        <div className="flex items-center space-x-2">
                                            <CheckCircle className="w-5 h-5 text-success-600" />
                                            <h4 className="font-medium text-success-900">
                                                Xem trước dữ liệu ({importData.length} học sinh)
                                            </h4>
                                        </div>
                                        <p className="text-sm text-success-700 mt-1">
                                            Dữ liệu đã được phân tích thành công. Vui lòng kiểm tra và xác nhận nhập.
                                        </p>
                                    </div>

                                    <div className="border border-ink-200 rounded-lg overflow-hidden">
                                        <div className="bg-ink-50 px-4 py-3 border-b border-ink-200">
                                            <h4 className="font-medium text-ink-900">
                                                Hiển thị 10 trong số {importData.length} học sinh
                                            </h4>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-ink-200">
                                                <thead>
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Họ</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Tên</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Giới tính</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Ngày sinh</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">SĐT</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Email</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-ink-200">
                                                    {importData.slice(0, 10).map((student, index) => (
                                                        <tr key={index} className="hover:bg-ink-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{student.firstName}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{student.lastName}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{student.gender}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{student.dateOfBirth}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{student.phone}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{student.email}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                                        <div className="flex items-center space-x-2">
                                            <CheckCircle className="w-5 h-5 text-success-600" />
                                            <h4 className="font-medium text-success-900">Nhập hoàn tất!</h4>
                                        </div>
                                        <p className="text-sm text-success-700 mt-1">
                                            Đã xử lý {importResults?.total || 0} học sinh: {importResults?.success?.length || 0} thành công, {importResults?.errors?.length || 0} lỗi
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-3 gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200">
                                        <div className="bg-white px-3 py-2.5">
                                            <div className="text-lg font-semibold tabular-nums text-success-700">{importResults?.success?.length || 0}</div>
                                            <div className="text-xs text-ink-500">Thành công</div>
                                        </div>
                                        <div className="bg-white px-3 py-2.5">
                                            <div className="text-lg font-semibold tabular-nums text-danger-700">{importResults?.errors?.length || 0}</div>
                                            <div className="text-xs text-ink-500">Lỗi</div>
                                        </div>
                                        <div className="bg-white px-3 py-2.5">
                                            <div className="text-lg font-semibold tabular-nums text-ink-900">{importResults?.total || 0}</div>
                                            <div className="text-xs text-ink-500">Tổng cộng</div>
                                        </div>
                                    </div>

                                    {importResults?.errors && importResults.errors.length > 0 && (
                                        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                                            <h5 className="font-medium text-danger-900 mb-3">Chi tiết lỗi:</h5>
                                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                                {importResults.errors.map((error: any, index: number) => (
                                                    <div key={index} className="text-sm text-danger-800 bg-danger-100 rounded p-2">
                                                        <span className="font-medium">Dòng {error.row}:</span> {error.error}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                </div>
                            )}
                </Modal>
            )}

            {/* Form Modal */}
            {showForm && (
                <Modal
                    open={showForm}
                    title={showEditModal ? 'Chỉnh sửa học sinh' : 'Thêm học sinh mới'}
                    onClose={closeStudentFormModal}
                    className="sm:max-w-2xl lg:max-w-2xl"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={closeStudentFormModal}>
                                Hủy
                            </Button>
                            <Button
                                onClick={() => handleSubmit(showEditModal)}
                                disabled={isSubmitting}
                                loading={isSubmitting}
                            >
                                {isSubmitting ? 'Đang lưu...' : showEditModal ? 'Cập nhật' : 'Thêm học sinh'}
                            </Button>
                        </div>
                    )}
                >
                    <div className="space-y-5">
                        <div className="grid gap-4 md:grid-cols-2">
                            <Input
                                label="Họ *"
                                value={formData.firstName}
                                onChange={(e) => {
                                    setFormData({ ...formData, firstName: e.target.value });
                                    if (formErrors.firstName) setFormErrors({ ...formErrors, firstName: '' });
                                }}
                                placeholder="Nguyễn Văn"
                                error={formErrors.firstName}
                            />
                            <Input
                                label="Tên *"
                                value={formData.lastName}
                                onChange={(e) => {
                                    setFormData({ ...formData, lastName: e.target.value });
                                    if (formErrors.lastName) setFormErrors({ ...formErrors, lastName: '' });
                                }}
                                placeholder="An"
                                error={formErrors.lastName}
                            />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <Select
                                label="Giới tính"
                                value={formData.gender}
                                onChange={(e) => {
                                    setFormData({ ...formData, gender: e.target.value });
                                    if (formErrors.gender) setFormErrors({ ...formErrors, gender: '' });
                                }}
                                error={formErrors.gender}
                            >
                                <option value="">Chọn giới tính</option>
                                <option value="Nam">Nam</option>
                                <option value="Nữ">Nữ</option>
                            </Select>
                            <DateInput
                                label="Ngày sinh"
                                value={formData.dateOfBirth}
                                max={todayDateKey()}
                                onChange={(value) => {
                                    setFormData({ ...formData, dateOfBirth: value });
                                    if (formErrors.dateOfBirth) setFormErrors({ ...formErrors, dateOfBirth: '' });
                                }}
                                error={formErrors.dateOfBirth}
                            />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <Input
                                label="Số điện thoại"
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="0123456789"
                                error={formErrors.phone}
                            />
                            <Input
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="student@example.com"
                                error={formErrors.email}
                            />
                        </div>

                        <Textarea
                            label="Địa chỉ"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Địa chỉ của học sinh..."
                            rows={2}
                        />

                        <div className="grid gap-4 md:grid-cols-2">
                            <Input
                                label="Tên phụ huynh"
                                value={formData.parentName}
                                onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                                placeholder="Nguyễn Văn B"
                            />
                            <Input
                                label="SĐT phụ huynh"
                                type="tel"
                                value={formData.parentPhone}
                                onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value })}
                                placeholder="0987654321"
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Class Modal */}
            {showAddClassModal && selectedStudent && (
                <Modal
                    open={showAddClassModal}
                    title={`Thêm lớp học cho ${selectedStudent.name}`}
                    onClose={closeAddClassModal}
                    className="sm:max-w-md lg:max-w-md"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={closeAddClassModal}>
                                Hủy
                            </Button>
                            <Button
                                onClick={handleAddStudentToClass}
                                disabled={!selectedClassToAdd}
                            >
                                Thêm vào lớp
                            </Button>
                        </div>
                    )}
                >
                    <div className="space-y-4">
                        <SearchSelect
                            label="Chọn lớp học *"
                            placeholder="Gõ tên lớp, môn, cơ sở hoặc giáo viên…"
                            value={selectedClassToAdd}
                            onChange={setSelectedClassToAdd}
                            options={classPickerOptions(availableClasses, { showCapacity: true })}
                            noResultsText="Không có lớp nào khớp"
                            hint={availableClasses.length === 0 ? 'Học sinh đã đăng ký tất cả các lớp học có sẵn.' : undefined}
                        />

                                {selectedClassToAdd && (
                                    <div className="bg-academy-50 border border-academy-200 rounded-lg p-4">
                                        {(() => {
                                            const selectedClass = availableClasses.find(c => c.id === selectedClassToAdd);
                                            if (!selectedClass) return null;

                                            return (
                                                <div className="space-y-2">
                                                    <h4 className="font-medium text-ink-900">Thông tin lớp học</h4>
                                                    <div className="text-sm text-academy-800 space-y-1">
                                                        <p><strong>Tên:</strong> {selectedClass.name}</p>
                                                        <p><strong>Môn học:</strong> {typeof selectedClass.subject === 'object' ? selectedClass.subject?.name : selectedClass.subject}</p>
                                                        <p><strong>Cơ sở:</strong> {typeof selectedClass.campus === 'object' ? selectedClass.campus?.name : 'Chưa có'}</p>
                                                        <p><strong>Giáo viên:</strong> {selectedClass.teacher || 'Chưa có'}</p>
                                                        <p><strong>Phòng:</strong> {selectedClass.room || 'Chưa có'}</p>
                                                        {/* <p><strong>Học phí:</strong> {(selectedClass.fee || 0).toLocaleString('vi-VN')}₫</p> */}
                                                        <p><strong>Sức chứa:</strong> {selectedClass.enrolled || 0}/{selectedClass.capacity || 0} học sinh</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                            </div>
                </Modal>
            )}
        </div>
    );
};

export default Students;
